/**
 * Schedule list page — `GET /schedules`.
 *
 * Follows the CoOrdio list-page pattern established by {@link ProjectListPage}: `ShellHeader` for
 * the title and primary action, `CordioAbpPagedDataTable` for the grid, one `DeleteDialog` driven by
 * `useDeleteDialog`, and a BEM block named after the page slug. The grid owns paging, search and
 * filtering, so the page keeps only `listVersion` — bumped after a mutation to rebuild `dataSource`
 * and make the grid refetch.
 *
 * ── COLUMN PLAN (field → header → width → cell type → filter) ──────────────────────────────────
 *   name            → Name     → 26% → text, clickable (row/cell click → schedule detail) → toolbar search
 *   agentName       → Agent    → 18% → text                                               → none (see below)
 *   scheduleType    → Type     → 12% → custom Chip (Cron / Interval / One-Time)            → none
 *   isActive        → Status   → 12% → custom Chip (Enabled / Disabled)                    → status tabs
 *   nextExecutionAt → Next Run → 16% → text via valueGetter, locale date + time            → none
 *   lastExecutionAt → Last Run → 16% → text via valueGetter, locale date + time            → none
 *                                      (widths sum to 100%)
 *
 * DROPPED from the old MUI table:
 *   • `description` — used to render as a caption under the name. A seventh column would push the
 *     grid past the 5–6 column budget; the description is on the detail and edit screens.
 *   • The old "Actions" column — row actions are now declarative (`actions` below), so the grid
 *     renders and aligns them.
 *
 * WHY no column is `sortable` and why there is only one popover filter: `ScheduleFilters` (the
 * service signature, which is the ground truth) is `pageNumber | pageSize | searchTerm |
 * agentDefinitionId | isActive`. `sortBy` and a schedule-type filter are not accepted, and
 * `scheduleService.getSchedules` builds its query string from that whitelist, so anything else
 * would be silently dropped — a control that looks like it filters but does not.
 *
 * The two clock columns are `type: 'text'` + `valueGetter` rather than `type: 'date'`: the built-in
 * date cell formats with `toLocaleDateString()`, and for a scheduler a "next run" without a time is
 * useless.
 */

import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Button,
  Chip,
  CordioAbpPagedDataTable,
  ShellHeader,
  type CordioDataTableAction,
  type CordioDataTableColumn,
  type CordioDataTableListToolbarConfig,
} from '@coordio/ui';
import { Plus, Trash2 } from 'lucide-react';

import {
  createCortexPagedDataSource,
  type CortexPagedQuery,
} from '../components/data-display/cortexPagedDataSource';
import { DeleteDialog, useDeleteDialog } from '../components/delete-dialog';
import { scheduleService } from '../services/scheduleService';
import { useNotificationStore } from '../store/notificationStore';
import { ScheduleTypes, type ScheduleDto, type ScheduleFilters, type ScheduleType } from '../types/schedule';
import { ROUTES } from '../utils/routes';

import './ScheduleListPage.scss';

/**
 * Quick-filter tabs.
 *
 * `value` is load-bearing twice: the grid writes it to the toolbar's `paramKey` AND builds each
 * tab's test id as `cordio-datatable-status-tab-<value>`. The All tab therefore declares the
 * literal `'all'` — an empty value would emit `cordio-datatable-status-tab-` and the E2E grid
 * checklist (GRID-13) would select nothing.
 *
 * `'all'` is a UI concept the API does not accept, which is why {@link STATUS_TAB_PARAM_KEY} is a
 * synthetic key that {@link toScheduleFilters} maps to `isActive` (and drops for All).
 */
const STATUS_TAB_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Enabled', value: 'true' },
  { label: 'Disabled', value: 'false' },
] as const;

/** The synthetic toolbar key the status tabs write to. Never sent to the API as-is. */
const STATUS_TAB_PARAM_KEY = 'status';

/** Existing product vocabulary for the DTO's `scheduleType` union. */
function formatScheduleType(type: ScheduleType): string {
  switch (type) {
    case ScheduleTypes.Cron:
      return 'Cron';
    case ScheduleTypes.Interval:
      return 'Interval';
    case ScheduleTypes.OneTime:
      return 'One-Time';
    default:
      return type;
  }
}

/** A schedule's clock values matter to the minute, so these keep the time the old page showed. */
function formatTimestamp(value: string | undefined, fallback: string): string {
  if (!value) return fallback;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

/**
 * Narrows the grid's query to exactly the keys `scheduleService.getSchedules` accepts.
 *
 * Written as an explicit whitelist rather than a spread so a future grid filter cannot leak a
 * parameter the endpoint would ignore. The synthetic `status` tab value becomes `isActive`; All
 * becomes "no filter at all" rather than a third state.
 */
function toScheduleFilters(query: CortexPagedQuery): ScheduleFilters {
  const status = query[STATUS_TAB_PARAM_KEY];
  const agentDefinitionId = query.agentDefinitionId;

  return {
    pageNumber: query.pageNumber,
    pageSize: query.pageSize,
    searchTerm: query.searchTerm,
    agentDefinitionId:
      typeof agentDefinitionId === 'string' && agentDefinitionId ? agentDefinitionId : undefined,
    isActive: status === 'true' ? true : status === 'false' ? false : undefined,
  };
}

export const ScheduleListPage = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotificationStore();

  /** Bumped after a mutation to rebuild `dataSource` and make the grid refetch. */
  const [listVersion, setListVersion] = useState(0);

  const { dialogRef, pendingItem, openDelete, closeDelete } = useDeleteDialog<ScheduleDto>();

  const dataSource = useMemo(
    () =>
      createCortexPagedDataSource<ScheduleDto>((query) =>
        scheduleService.getSchedules(toScheduleFilters(query)),
      ),
    // `listVersion` is a deliberate dependency: changing the identity of `dataSource` is the
    // documented way to make the grid reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listVersion],
  );

  const columns = useMemo<ReadonlyArray<CordioDataTableColumn<ScheduleDto>>>(
    () => [
      { field: 'name', header: 'Name', type: 'text', width: '26%', clickable: true },
      { field: 'agentName', header: 'Agent', type: 'text', width: '18%' },
      {
        field: 'scheduleType',
        header: 'Type',
        type: 'custom',
        width: '12%',
        customCell: (row) => <Chip label={formatScheduleType(row.scheduleType)} variant="outlined" />,
      },
      {
        field: 'isActive',
        header: 'Status',
        type: 'custom',
        width: '12%',
        // A Chip rather than the built-in boolean cell, so the row reads with the same vocabulary
        // as the status tabs and the enable/disable endpoints behind the row actions.
        customCell: (row) => (
          <Chip label={row.isActive ? 'Enabled' : 'Disabled'} color={row.isActive ? 'green' : 'default'} />
        ),
      },
      {
        field: 'nextExecutionAt',
        header: 'Next Run',
        type: 'text',
        width: '16%',
        valueGetter: (row) => formatTimestamp(row.nextExecutionAt, 'N/A'),
      },
      {
        field: 'lastExecutionAt',
        header: 'Last Run',
        type: 'text',
        width: '16%',
        valueGetter: (row) => formatTimestamp(row.lastExecutionAt, 'Never'),
      },
    ],
    [],
  );

  const listToolbar = useMemo<CordioDataTableListToolbarConfig>(
    () => ({
      search: { placeholder: 'Search schedules by name' },
      statusTabs: {
        paramKey: STATUS_TAB_PARAM_KEY,
        ariaLabel: 'Schedule status filter',
        options: [...STATUS_TAB_OPTIONS],
      },
      // The only other filter `GET /schedules` accepts. It is the agent's id rather than its name
      // because that is what the endpoint takes.
      filters: [
        {
          key: 'agentDefinitionId',
          label: 'Agent',
          type: 'text',
          placeholder: 'Agent definition id',
          description: 'Exact agent definition id, as shown on the agent detail page.',
        },
      ],
      filterButtonLabel: 'Filter',
    }),
    [],
  );

  const handleToggleActive = useCallback(
    async (schedule: ScheduleDto) => {
      try {
        if (schedule.isActive) {
          await scheduleService.disableSchedule(schedule.id);
          showNotification('Schedule disabled successfully', 'success');
        } else {
          await scheduleService.enableSchedule(schedule.id);
          showNotification('Schedule enabled successfully', 'success');
        }
        setListVersion((v) => v + 1);
      } catch (err) {
        showNotification(err instanceof Error ? err.message : 'Failed to update schedule', 'error');
      }
    },
    [showNotification],
  );

  const actions = useMemo<ReadonlyArray<CordioDataTableAction<ScheduleDto>>>(
    () => [
      {
        icon: 'eye',
        tooltip: 'View schedule',
        onClick: (row) => navigate(ROUTES.SCHEDULE_DETAIL.replace(':id', row.id)),
      },
      {
        icon: 'square-pen',
        tooltip: 'Edit schedule',
        onClick: (row) => navigate(ROUTES.SCHEDULE_EDIT.replace(':id', row.id)),
      },
      // Two declarative actions with `visible` predicates rather than one action that changes
      // meaning: the icon then always matches what clicking it does.
      {
        icon: 'pause',
        tooltip: 'Disable schedule',
        visible: (row) => row.isActive,
        onClick: (row) => void handleToggleActive(row),
      },
      {
        icon: 'play',
        tooltip: 'Enable schedule',
        visible: (row) => !row.isActive,
        onClick: (row) => void handleToggleActive(row),
      },
      {
        icon: 'trash-2',
        tooltip: 'Delete schedule',
        className: 'schedule-list__action--destructive',
        onClick: (row) => openDelete(row),
      },
    ],
    [navigate, handleToggleActive, openDelete],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!pendingItem) return;

    await scheduleService.deleteSchedule(pendingItem.id);
    showNotification('Schedule deleted successfully', 'success');
    setListVersion((v) => v + 1);
  }, [pendingItem, showNotification]);

  return (
    <div className="schedule-list" data-testid="schedule-list-page">
      <ShellHeader
        title="Schedules"
        description="Recurring and one-off agent runs, with the next and last execution of each."
        data-testid="schedule-list-header"
        actions={
          <Button
            type="button"
            variant="default"
            size="sm"
            className="gap-2"
            data-testid="add-schedule-btn"
            onClick={() => navigate(ROUTES.SCHEDULE_NEW)}
          >
            <Plus className="h-4 w-4 shrink-0" aria-hidden />
            Create Schedule
          </Button>
        }
      />

      <div data-testid="schedule-list-grid">
        <CordioAbpPagedDataTable<ScheduleDto>
          className="schedule-list__table"
          columns={columns}
          dataSource={dataSource}
          initialPageSize={25}
          maxBodyHeight="520px"
          listToolbar={listToolbar}
          actions={actions}
          onRowClick={(row) => navigate(ROUTES.SCHEDULE_DETAIL.replace(':id', row.id))}
        />
      </div>

      <DeleteDialog
        ref={dialogRef}
        title="Delete schedule?"
        entityLabel={pendingItem?.name}
        dialogIcon={<Trash2 className="delete-dialog__icon" />}
        description="The schedule stops running and its execution history is no longer reachable from here. This cannot be undone."
        confirmLabel="Delete"
        confirmVariant="destructive"
        busyLabel="Deleting…"
        onConfirm={handleDeleteConfirm}
        onCancel={closeDelete}
      />
    </div>
  );
};
